/**
 * Validated environment variables for chaos-ui.
 *
 * Vite exposes `import.meta.env.VITE_*` at build/dev time. Anything not
 * prefixed `VITE_` is stripped from the bundle for safety.
 *
 * NOTE (2026-05-20, bundle-split pass): we used to validate this with zod.
 * That pulled the entire zod chunk (~50KB) into the eager bundle even though
 * the only consumers needed three string defaults. The minimal parser below
 * does the same job in ~20 lines with no dependencies — keeping zod out of
 * the form-vendor preload list.
 */

function readString(key: string, fallback: string): string {
  const raw = (import.meta.env as Record<string, unknown>)[key];
  return typeof raw === "string" && raw.length > 0 ? raw : fallback;
}

function readMode(): "development" | "production" | "test" {
  const m = (import.meta.env as Record<string, unknown>).MODE;
  if (m === "production" || m === "test") return m;
  return "development";
}

// Strict literal-'true' flag (mirrors the gateway idiom kb-search.ts:73 /
// config-copy.ts isCrossOrgCopyEnabled): anything but the literal string 'true'
// (unset/empty/'1'/'TRUE') = OFF.
function readBoolFlag(key: string): boolean {
  return (import.meta.env as Record<string, unknown>)[key] === "true";
}

export interface Env {
  // Gateway base URL — usually same-origin in production (Caddy proxies
  // /api/* to chaos-gateway), but split out so dev can hit localhost:8100.
  VITE_API_BASE_URL: string;
  // WebSocket base URL — same shape as VITE_API_BASE_URL.
  VITE_WS_BASE_URL: string;
  // VAPID public key (URL-safe base64) — used to subscribe browsers to
  // Web Push. The matching private key lives in Infisical and is only
  // visible to chaos-openclaw-bridge. Empty string disables push UX.
  VITE_VAPID_PUBLIC_KEY: string;
  // W2-6 invite/direct-add UI. DEFAULT OFF and deliberately separate from the
  // gateway probe: this is the reviewed build-time activation stop (including
  // unresolved SPEC-02 Q4), while the successful invitations-list probe proves
  // the running gateway also has INVITES + atomic offboarding enabled. Both
  // must be true before any second-human affordance mounts.
  VITE_INVITES: boolean;
  // Org Architecture Phase G — gate the Scoped-Access UI (Access & Governance
  // tab, Scope Card, Effective-access viewer). DEFAULT OFF. Pairs with the
  // gateway SCOPED_ACCESS_ENABLED flag: enforcement only happens server-side
  // when the gateway flag is ON, but this UI can be shown to STAGE grants
  // before enforcement is flipped on. The screens degrade gracefully on a 404
  // (gateway route absent / older build) so a UI/gateway mismatch never errors.
  VITE_SCOPED_ACCESS_ENABLED: boolean;
  // KB Access P6 — gate the per-LEVEL authoring tabs (the Org/Project/Board/
  // Card "Context" panel: Instructions | Knowledge Base | Brain) AND the
  // KB-authoring affordances inside the Access surfaces. DEFAULT OFF. When OFF
  // every per-level Context tab + KB-authoring control renders a disabled notice
  // (or does not mount) so the existing screens are BYTE-IDENTICAL; the
  // authoring surfaces mount ONLY when 'true'. Independent of
  // VITE_SCOPED_ACCESS_ENABLED (which gates the grant/inspector/delegation
  // surfaces); the two compose cleanly. Pairs with the gateway
  // KB_LEVEL_SCOPE_ENABLED flag (server-derived scope on read/write) + the
  // access-guarded instruction routes; the UI degrades gracefully on a 403/404
  // so a UI/gateway flag mismatch never errors.
  VITE_KB_LEVEL_AUTHORING: boolean;
  // UI Polish (Wave 1) — gate the dual-first polish pass: the rebuilt status
  // panel (Codex/Claude vertical-spine + remaining-bars, responsive on mobile),
  // the neutral-emphasis + mobile-collapsed Claude re-auth banner, and the
  // single-column settings shell at <768px. DEFAULT OFF. When OFF the status
  // panel / banner / settings shell render BYTE-IDENTICAL to today (every
  // polish branch is gated on this literal-'true' flag), so a flag-OFF build is
  // unchanged from production. Independent of the other flags — composes cleanly
  // on top of the (now permanent) Settings V2/V3 surfaces.
  VITE_UI_POLISH: boolean;
  // Unified Trash — gate the delete-to-trash affordances (the ⋯ menu + confirm
  // dialog on project/board rows in the Organizations & projects tab) AND the
  // account-level Admin → Trash section (list/restore/permanent-delete of
  // archived orgs/projects/boards). DEFAULT OFF. When OFF the
  // Organizations & projects tab + settings render BYTE-IDENTICAL to today
  // (every Trash branch is gated on this literal-'true' flag); the menus +
  // Trash section mount ONLY when 'true'. Pairs with the gateway TRASH_ENABLED
  // flag, which gates the restore / permanent-delete WRITE paths server-side —
  // this UI degrades gracefully on a 404 (gateway flag OFF / older build) so a
  // UI/gateway flag mismatch never errors.
  VITE_TRASH: boolean;
  // External links (migration 0074) — gate the public file-share UI: the
  // account-scoped "External links" settings tab (grouped list of active share
  // links with copy/revoke) AND the per-file Share popover in the Artifacts tab
  // + Explorer (create/copy/revoke a "anyone with the link can view" URL).
  // DEFAULT OFF. When OFF the settings nav + Artifacts/Explorer render
  // BYTE-IDENTICAL to today (every External-links branch is gated on this
  // literal-'true' flag); the tab + popover mount ONLY when 'true'. Pairs with
  // the gateway external-links API + token-gated /p/f/:token serve route, which
  // are account-owner / org-admin gated server-side, so the UI degrades
  // gracefully on a 403/404 (caller not an owner / older gateway) and never errors.
  VITE_EXTERNAL_LINKS: boolean;
  // Session attachments (Phase 1) — gate the CARD session composer "+" that
  // attaches files to a single message. Files upload to the card KB store
  // (POST /cards/:id/knowledge — same store/mount/metadata pipeline), show as
  // chips in the composer, and on send their mounted /knowledge/card/ paths are
  // handed to the agent. DEFAULT OFF. When OFF the composer renders
  // BYTE-IDENTICAL to today (no "+", no chips) — every attachment branch is
  // gated on this literal-'true' flag. Pairs with the gateway
  // SESSION_ATTACHMENTS_ENABLED flag (which injects the mounted paths).
  VITE_SESSION_ATTACHMENTS: boolean;
  // Artifacts touched this run — gate the "Artifacts" footer rendered under a
  // finalized agent message (the DRY sources-style row of files the run
  // created/updated) AND the ?artifact=<path> deep-link that opens the Artifacts
  // tab focused on that file. DEFAULT OFF. When OFF every agent bubble +
  // Artifacts tab render BYTE-IDENTICAL to today (both the footer render and the
  // deep-link consumption are gated on this literal-'true' flag), so a flag-OFF
  // build is unchanged. Pairs with the gateway SESSION_ARTIFACTS_FOOTER_ENABLED
  // flag (which persists metadata.artifacts on the final agent message); when
  // the gateway flag is OFF the metadata is simply absent and the footer never
  // has anything to render — the two compose cleanly.
  VITE_SESSION_ARTIFACTS_FOOTER: boolean;
  // Conductor rich render (founder mandate 2026-07-26) — gate the two ADDITIVE
  // surfaces in the Conductor drawer: the activity strip between the transcript
  // and the composer (pulsing dot, elapsed clock, humanised tool chips,
  // reasoning tail) and the "Created" receipts footer under a turn (real card
  // titles / column / priority / org, read from chaos.cards rather than scraped
  // from the reply prose). DEFAULT OFF. When OFF the drawer renders as it does
  // today: no strip, and the receipts block degrades to the prose-scraped "Open
  // card" chips with no header — every rich branch is gated on this
  // literal-'true' flag. NOT gated, because their worst case IS today's render:
  // the markdown bubble (its Suspense fallback is the current plaintext block),
  // the internal-link interceptor, the socket keep-alive, and the terminal-note
  // relocation. No gateway pairing — the receipts query fails soft on its own,
  // and an absent `artifacts` field simply leaves the prose fallback in charge.
  VITE_CONDUCTOR_RICH_RENDER: boolean;
  // Session chrome V2 — gate the compact session shell: the one-row card-detail
  // header, the 3-state composer (short resting / deferred-list / typing reflow),
  // the relocated Q/Interrupt + deferred controls, the ⋯ menu regrouping (Autonomy
  // + Vendor reachable on mobile), and the context-% ring moved into the activity
  // dock. DEFAULT OFF. When OFF the header, composer, and dock render
  // BYTE-IDENTICAL to today (every chrome-V2 branch is gated on this literal-'true'
  // flag, keeping the old code path intact). No gateway pairing — purely a UI
  // reflow of existing controls, each keeping its handler + persistence.
  VITE_SESSION_CHROME_V2: boolean;
  // Role Continuous Learning (migration 0112) — gate the role "Training" section:
  // the Proposals inbox + git-log History rendered inside the role editor drawer
  // (RoleEditDrawer, shared by the legacy company/roles page and the account-wide
  // roles-matrix-v2 slide-over) AND the pending-count badges on role rows.
  // DEFAULT OFF. When OFF the role editor + role lists render BYTE-IDENTICAL to
  // today (every Training branch is gated on this literal-'true' flag); the
  // section + badges mount ONLY when 'true'. Pairs with the gateway
  // ROLE_LEARNING_ENABLED flag, which gates the propose/decide/revert/history
  // routes + the learned-layer prompt injection server-side — this UI degrades
  // gracefully on a 404 (gateway flag OFF / older build) so a UI/gateway flag
  // mismatch never errors.
  VITE_ROLE_LEARNING: boolean;
  // Scoped Knowledge Routing (spec CHAOS_KNOWLEDGE_ROUTING_ARCHITECTURE.md Part
  // B, migration 0113 chaos.knowledge_additions) — gate the scope-generic
  // knowledge review sections: the Proposals inbox + git-log History rendered
  // under Settings → Organization ("Organization knowledge") and Settings →
  // Boards/Project ("Project knowledge"), the re-target control, and the
  // pending-count badges. DEFAULT OFF. When OFF the knowledge admin page renders
  // BYTE-IDENTICAL to today (every review branch is gated on this literal-'true'
  // flag); the sections mount ONLY when 'true'. Pairs with the gateway
  // KNOWLEDGE_ROUTING_ENABLED flag, which gates the chaos.remember dispatch +
  // /api/knowledge/:scope/:targetId/additions routes + the project/org learned-
  // layer prompt injection server-side — this UI degrades gracefully on a 404
  // (gateway flag OFF / older build) so a UI/gateway flag mismatch never errors.
  VITE_KNOWLEDGE_ROUTING: boolean;
  // Design V2 "Liquid Graphite" (ADR-057 v2) — gate the design refresh:
  // self-hosted Geist (UI chrome) + Bricolage Grotesque (board/screen titles)
  // finally load (@font-face in styles/design-v2.css, /public/fonts woff2,
  // no CDN); the kanban columns become sunken NEUTRAL wells with cards as
  // raised paper (one soft shadow); the Kanban/List switcher becomes a
  // sunken-track/raised-thumb segmented control; card titles gain weight;
  // list-view group headers drop the SCREAMING-caps treatment; the black
  // focus ring and the perpetual working-glyph pulse are retired; borders +
  // secondary text pick up a graphite temperature. Canvas/panels stay
  // NEUTRAL (pure white light / unchanged dark gray) — the founder rejected
  // any blue-tinted base surface. Chat/agent-message prose keeps today's
  // font (pinned in design-v2.css). DEFAULT OFF. When OFF, main.tsx never
  // sets html[data-design='v2'], every token/rule in design-v2.css is inert,
  // neither font downloads, and every component branch keeps the original
  // class strings — the OFF build renders BYTE-IDENTICAL to today. Purely a
  // UI restyle: no layout moves, no gateway pairing.
  VITE_DESIGN_V2: boolean;
  // Design Lightening layer (design-overhaul master spec R1) — gates the whole
  // "significantly lighter" pass ON TOP of the (now-permanent) Design V2: the
  // global token lightening (quieter hairlines / edge-OR-elevation primitives /
  // metadata diet), the ListView + Inbox + Files + Settings restyles, the
  // Kanban+Cards runtime A/B machinery (html[data-kanban-ab]), the R5 auto-
  // collapse control, and the R3 card meta line. DEFAULT OFF. When OFF, main.tsx
  // never sets html[data-light]/[data-kanban-ab], every rule in styles/light/*
  // is inert (scoped under :root[data-light]), and every component branch keeps
  // the current v2 class strings — the OFF build renders BYTE-IDENTICAL to
  // today. Kanban+Cards additionally carry a runtime old↔new switch inside this
  // flag; ListView/Inbox/Files/Settings are a full replace under it.
  VITE_DESIGN_LIGHT: boolean;
  // Brain overhaul (master spec R4/§9) — gate the Brain near-rebuild: openable/
  // previewable sources (the in-app reader route), in-app citation/media/entity
  // navigation, and the lightened Library surface. DEFAULT OFF. When OFF the
  // Brain area renders BYTE-IDENTICAL to today (every Brain-V2 branch is gated
  // on this literal-'true' flag). Pairs with the gateway BRAIN_SOURCE_READ_ENABLED
  // flag (GET /api/brain/source/:id): when the gateway flag is OFF the route
  // 404s and the UI degrades to today's behavior, so a UI/gateway flag mismatch
  // never errors.
  VITE_BRAIN_V2: boolean;
  // Comm Center (master spec R4/§12, canon CHAOS_COMM_CENTER_SPEC) — gate the
  // whole Communications area (channels, 3-pane inbox, drafts queue, unsorted
  // lane, rules editor). Built light-from-birth under styles/light/. DEFAULT
  // OFF. When OFF the Comms area does not mount and the rest of the app renders
  // BYTE-IDENTICAL to today. Landed in Cycle 2; declared now so the flag exists
  // for the shell/nav wiring.
  VITE_COMM_CENTER: boolean;
  // Comm Center stage 3 (SPEC-07 §3) — gate the WORKSPACE layer of the channels
  // screens: the Workspace picker + plan-limit counter in the Add-Channel
  // wizard, the Workspace grouping + per-Workspace suspension banner on the
  // channels list, and the Access tab's read-only who-sees-what panel. DEFAULT
  // OFF. When OFF none of those pieces MOUNT — not "render nothing", do not
  // mount — so the channels screens are the shipped ones VERBATIM and the extra
  // reads (GET /api/comms/meta, the orgs list) are never issued at all. Nests
  // INSIDE VITE_COMM_CENTER: with the Comm Center off there is no channels
  // screen for this to change. Pairs with the gateway's GET /api/comms/meta and
  // its COMM_MT_ENABLED flag — meta answers `planEnforced:false` while that
  // gateway flag is off, and the counter then reads as INFORMATIONAL rather
  // than as a gate, because announcing an enforcement that is not there is the
  // one direction it is dangerous to be wrong in. The meta read degrades to
  // null on a 404, so a UI-on/gateway-old build falls back to today's screen
  // instead of erroring.
  VITE_COMM_WORKSPACE_CHANNELS: boolean;
  // Card merge (migrations 0117 + 0120) — gate the complete merge surface:
  // the card action, merge wizard, and provenance strip. DEFAULT OFF. When
  // OFF, no merge query or control mounts and card detail stays unchanged.
  // Pairs with the gateway CARD_MERGE_ENABLED flag; both must be literal
  // 'true' for the feature to work end-to-end.
  VITE_CARD_MERGE: boolean;
  // Broker session import (CHAOS Connect, migration 0119 — Secure Browser
  // Broker "connect via browser") — gate the whole "Connect via browser"
  // affordance on a website credential's detail: the button, the install +
  // how-to modal, the "Generate pairing code" flow (POST
  // /credentials/:id/connect-code) and the session-status readout (GET
  // /credentials/:id/session-status). The founder installs the CHAOS Connect
  // MV3 extension once and pastes a single-use pairing code; the extension
  // hands the live logged-in session (cookies + storageState) to the broker,
  // which stores it encrypted — the agent never sees it (Stage-3 invariant).
  // DEFAULT OFF. When OFF the credential detail/list render BYTE-IDENTICAL to
  // today (the button + modal are gated on this literal-'true' flag and never
  // mount). Pairs with the gateway connect-code / session-status routes; the UI
  // degrades gracefully on a 404 (gateway route absent / older build) so a
  // UI/gateway mismatch never errors.
  VITE_BROKER_SESSION_IMPORT: boolean;
  // Marketplace (SPEC-13 / SPEC-16) — gate the whole /market area: the
  // storefront, the pack card, the expert page, checkout, and the seller
  // profile in Settings. DEFAULT OFF. When OFF every /market* route redirects
  // Home in beforeLoad and the rail never renders a Market icon, so an OFF
  // build is byte-identical to the one before this flag existed.
  // Pairs with the gateway MARKETPLACE_ENABLED flag, which answers 404
  // feature_disabled on all ten routes when it is off — so a UI-on/gateway-off
  // build shows an honest "not enabled on this instance" panel rather than an
  // error boundary. NOTHING IN THIS AREA CAN CHARGE ANYBODY: there is no
  // payment processor on this platform and every screen says so out loud.
  VITE_MARKETPLACE: boolean;
  // Skills Store is a separate build-time surface (SPEC-16). Either baked
  // master may mount the shared Market shell; every runtime child control is
  // still decided by the authenticated server capability DTO.
  VITE_SKILLS_STORE: boolean;
  // University (wave 6) — gate the rail entry for the /university area: the
  // skill catalogue, tuners, training sessions, pattern clusters, A/B tests
  // and the per-Workspace skill listings. DEFAULT OFF. When OFF the rail never
  // renders a University icon and no University code is reachable from the
  // chrome, so an OFF build is byte-identical to the one before this flag
  // existed. Unlike /market there is no layout route to hang a beforeLoad on —
  // the thirteen screens are siblings under /university/* — so this flag gates
  // the ENTRY, and a surviving deep link still resolves. That is deliberate
  // and not a hole: every screen is fenced server-side on the active Workspace
  // (`?orgId=`, apps/chaos-gateway/src/learning.ts) and answers 404 for a
  // Workspace the caller is not in, so what a deep link reaches is what that
  // caller was already entitled to read. No gateway flag pairs with this one;
  // when the University module is absent from a build the screens say so
  // (isRouteMissing) rather than erroring.
  VITE_UNIVERSITY: boolean;
  // Knowledge & IP (SPEC-08 Stage 2 / W3-17) is split into three independently
  // deployable server capabilities, but shares one honest Workspace settings
  // surface. Each flag defaults OFF. When all three are OFF there is no rail
  // item, lazy import, route or query; the existing Settings tree is unchanged.
  // TRANSFER_POLICY exposes the owner policy + exact-object decision queue,
  // CONNECTION_MODES exposes consent-backed member terms/proposals, and
  // ROLE_CONNECTIONS exposes connected-role retention/snapshot state.
  VITE_TRANSFER_POLICY: boolean;
  VITE_CONNECTION_MODES: boolean;
  VITE_ROLE_CONNECTIONS: boolean;
  // Workspace share profiles (SPEC-11 / W3-18) -- gate the Sharing card in
  // Settings -> Workspace: what is currently opened into this Workspace
  // (roles, skills, connectors, permissions, templates, automation rules,
  // credentials), whether each mark was placed by a human or arrived through
  // a rule, and the posture control that decides whether a NEW Workspace
  // inherits anything at all. DEFAULT OFF: when OFF the card does not mount
  // and the page is byte-identical to the one before this flag existed.
  // Pairs with the gateway SHARE_PROFILES_ENABLED flag, which 404s all four
  // routes when it is off -- so a UI-on/gateway-off build shows an honest
  // "not enabled on this instance" line rather than an error boundary.
  VITE_SHARE_PROFILES: boolean;
  // SPEC-14 Resources (Settings -> Resources): the model-source catalog,
  // the models each source offers, and the routing rules -- which are
  // evaluated in SHADOW and steer nothing. Pairs with three gateway flags
  // that each answer 404 when off (MODEL_SOURCES_ENABLED,
  // MODEL_ROUTING_RULES_ENABLED, WORKSPACE_RESOURCE_SPONSORSHIP_ENABLED),
  // so the page can tell "nothing configured" from "the server has it off".
  VITE_RESOURCES_HUB: boolean;
  // SPEC-12 external guard — gate the supervision screen (Settings ->
  // Supervision): the guard-event feed, the "frozen right now" block, and the
  // release control. DEFAULT OFF. When OFF the tab is absent from
  // settings-nav.tsx and the route redirects in beforeLoad, so the settings
  // shell is byte-identical to the one before this flag existed. Pairs with
  // the gateway MEMBER_SUPERVISION_ENABLED flag, which answers 404
  // feature_disabled on the feed and on both containment routes when it is
  // off -- so a UI-on/gateway-off build shows an honest "not enabled on this
  // instance" panel rather than an error boundary. The release control is
  // additionally gated on GET /orgs/:id/guard-policy: it mounts only for a
  // viewer the policy's unfreeze authority actually names, and not at all when
  // that read is refused or unreadable.
  VITE_EXTERNAL_GUARD: boolean;
  // First-run onboarding (SPEC-09 3.5 / W3-23) -- gate the /welcome wizard
  // (workspace -> AI sign-in -> first card), the "Setup guide" item in the
  // sidebar account menu, and the invited-member first-session checklist on
  // /boards. DEFAULT OFF. When OFF the wizard route redirects to /boards in
  // beforeLoad, the menu item does not render, the checklist does not mount and
  // no onboarding code path can be reached -- an OFF build is byte-identical to
  // the one before this flag existed. NO GATEWAY PAIRING, deliberately: the
  // wizard has no endpoint of its own. Every step reads an EXISTING surface
  // (GET /api/orgs, GET /api/vendors/status, the kanban card create) and each
  // one degrades to a StateCard on a 403/404, so the SPEC-03 flags being off is
  // a state the wizard renders rather than an error it throws. Its own memory
  // is localStorage under `chaos.onboarding.v1.<userId>` (SPEC-09 3.5.5) --
  // there is no migration and no server write anywhere in this feature.
  VITE_ONBOARDING: boolean;

  /**
   * W4-12/W4-13. Settings -> Usage & plan: what this account has spent against
   * every ceiling it has, which one it will hit first, and whether any of them
   * is being applied.
   *
   * IT GATES THE REPORT, NOT THE CEILING. The three gates that can actually
   * refuse work live in the GATEWAY's environment (EMBED_QUOTAS_ENFORCED,
   * BILLING_ENFORCE_SEATS, BILLING_ENFORCE_STORAGE) and are unaffected by this.
   * Turning this off hides the screen; a limit that was biting keeps biting,
   * and the person it bites loses the only page that would have told them why.
   * That is the argument for shipping it ON while every gate is off.
   *
   * NOTHING BEHIND IT TAKES MONEY. The page has no card form and no checkout —
   * see the payment panel in usage-plan-page.tsx.
   */
  VITE_BILLING: boolean;

  /**
   * M2 — the payment affordances: "Pay" on the checkout screen, the refund button on an
   * order, the earnings screen and the seller's processor-onboarding section.
   *
   * DEFAULT OFF, and OFF IS THE HONEST STATE TODAY. The gateway's own commerce ladder is
   * off on every box, so with this on the screens would render buttons whose routes
   * answer `404 feature_disabled` — a control that does nothing teaches a person the
   * product is broken.
   *
   * THE TWO HANDLES ARE NOT ONE. This flag is baked into the bundle at build time, so
   * changing it needs a UI rebuild (`chaos-safe-deploy.sh bake ui`); the server's
   * `SKILLS_STORE_PAYMENTS_ENABLED` lives in the gateway's process and needs a recreate.
   * A recreate without a rebuild moves the server and not the screen, and that skew has
   * already been paid for once — `--flag-rollout` plus an image baseline in pre-flight is
   * what keeps the pair moving together.
   *
   * WHAT IT DOES NOT GATE: any money rule. The commission, the refund window, the hold
   * and the triple lock on live money are all the server's, and none of them can be
   * reached from this bundle. Turning this on shows buttons; it arms nothing.
   */
  VITE_PAYMENTS: boolean;

  /**
   * W2-5 / SPEC-04 §6.1 + §6.3. The execution settings a HUMAN can change:
   * the card's Execution section (run ceiling, the used-since-last-human
   * counter and its Reset button, Always on, Sleep when idle) and the
   * account-scope Settings -> Execution page that owns the defaults those
   * inherit from.
   *
   * DEFAULT OFF, and OFF IS NOT "NOTHING". The card Settings tab has carried a
   * run-ceiling stepper since the ceiling shipped, and that stepper stays
   * exactly as it is when this flag is off -- the flag chooses between the
   * shipped control and the fuller section that CONTAINS it, never between a
   * control and an empty div. A capability that exists today does not disappear
   * because a new flag defaults off.
   *
   * WHAT IS GENUINELY NEW BEHIND IT: the always-on pin, the sleep-when-idle
   * select, the reset button, and the whole account page. Each of those edits a
   * column that is LIVE -- `cards.always_on` is read by the idle reaper and
   * `accounts.card_max_consecutive_runs` is the default every card inherits --
   * so the flag is what keeps a half-reviewed screen from handing those out
   * before the founder has looked at it.
   *
   * NO GATEWAY FLAG PAIRS WITH THIS ONE, deliberately, and the routes say why:
   * they edit columns that already decide behaviour, so there is nothing for a
   * server-side switch to protect that turning the screen off does not. Two of
   * the numbers the screens SHOW are gated at the server, and are reported as
   * such rather than hidden: `flags.runCeilingEnforced` (CARD_CEILINGS_ENABLED)
   * and `flags.idleStop` (IDLE_STOP_ENABLED) travel in the payload so the page
   * can say "configured but not currently enforced" instead of implying a brake
   * that is not biting. `flags.slotMetering` does the same for the slots meter,
   * whose count is 0 while nothing is counting.
   */
  VITE_EXECUTION_SETTINGS: boolean;
  // Build mode (set by Vite automatically).
  MODE: "development" | "production" | "test";
}

export const env: Env = {
  VITE_API_BASE_URL: readString("VITE_API_BASE_URL", "/api"),
  VITE_WS_BASE_URL: readString("VITE_WS_BASE_URL", "/ws"),
  VITE_VAPID_PUBLIC_KEY: readString("VITE_VAPID_PUBLIC_KEY", ""),
  VITE_INVITES: readBoolFlag("VITE_INVITES"),
  VITE_SCOPED_ACCESS_ENABLED: readBoolFlag("VITE_SCOPED_ACCESS_ENABLED"),
  VITE_KB_LEVEL_AUTHORING: readBoolFlag("VITE_KB_LEVEL_AUTHORING"),
  VITE_UI_POLISH: readBoolFlag("VITE_UI_POLISH"),
  VITE_TRASH: readBoolFlag("VITE_TRASH"),
  VITE_EXTERNAL_LINKS: readBoolFlag("VITE_EXTERNAL_LINKS"),
  VITE_SESSION_ATTACHMENTS: readBoolFlag("VITE_SESSION_ATTACHMENTS"),
  VITE_SESSION_ARTIFACTS_FOOTER: readBoolFlag("VITE_SESSION_ARTIFACTS_FOOTER"),
  VITE_CONDUCTOR_RICH_RENDER: readBoolFlag("VITE_CONDUCTOR_RICH_RENDER"),
  VITE_SESSION_CHROME_V2: readBoolFlag("VITE_SESSION_CHROME_V2"),
  VITE_ROLE_LEARNING: readBoolFlag("VITE_ROLE_LEARNING"),
  VITE_KNOWLEDGE_ROUTING: readBoolFlag("VITE_KNOWLEDGE_ROUTING"),
  VITE_DESIGN_V2: readBoolFlag("VITE_DESIGN_V2"),
  VITE_DESIGN_LIGHT: readBoolFlag("VITE_DESIGN_LIGHT"),
  VITE_BRAIN_V2: readBoolFlag("VITE_BRAIN_V2"),
  VITE_COMM_CENTER: readBoolFlag("VITE_COMM_CENTER"),
  VITE_COMM_WORKSPACE_CHANNELS: readBoolFlag("VITE_COMM_WORKSPACE_CHANNELS"),
  VITE_CARD_MERGE: readBoolFlag("VITE_CARD_MERGE"),
  VITE_BROKER_SESSION_IMPORT: readBoolFlag("VITE_BROKER_SESSION_IMPORT"),
  VITE_MARKETPLACE: readBoolFlag("VITE_MARKETPLACE"),
  VITE_SKILLS_STORE: readBoolFlag("VITE_SKILLS_STORE"),
  VITE_UNIVERSITY: readBoolFlag("VITE_UNIVERSITY"),
  VITE_TRANSFER_POLICY: readBoolFlag("VITE_TRANSFER_POLICY"),
  VITE_CONNECTION_MODES: readBoolFlag("VITE_CONNECTION_MODES"),
  VITE_ROLE_CONNECTIONS: readBoolFlag("VITE_ROLE_CONNECTIONS"),
  VITE_SHARE_PROFILES: readBoolFlag("VITE_SHARE_PROFILES"),
  VITE_RESOURCES_HUB: readBoolFlag("VITE_RESOURCES_HUB"),
  VITE_EXTERNAL_GUARD: readBoolFlag("VITE_EXTERNAL_GUARD"),
  VITE_ONBOARDING: readBoolFlag("VITE_ONBOARDING"),
  VITE_BILLING: readBoolFlag("VITE_BILLING"),
  VITE_PAYMENTS: readBoolFlag("VITE_PAYMENTS"),
  VITE_EXECUTION_SETTINGS: readBoolFlag("VITE_EXECUTION_SETTINGS"),
  MODE: readMode(),
};
